// Application status constants and utilities
// Standardized application statuses for the InfluenceHub platform

/**
 * Valid application statuses
 * - pending_review: Application is awaiting brand review
 * - accepted: Application has been accepted by the brand
 * - rejected: Application has been rejected by the brand
 */
export const APPLICATION_STATUSES = {
  PENDING_REVIEW: 'pending_review',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
};

/**
 * Default status for new applications
 */
export const DEFAULT_APPLICATION_STATUS = APPLICATION_STATUSES.PENDING_REVIEW;

/**
 * Valid statuses that brands can set when updating applications
 */
export const BRAND_UPDATEABLE_STATUSES = [
  APPLICATION_STATUSES.ACCEPTED,
  APPLICATION_STATUSES.REJECTED,
];

/**
 * Status display labels
 */
export const STATUS_LABELS = {
  [APPLICATION_STATUSES.PENDING_REVIEW]: 'Pending Review',
  [APPLICATION_STATUSES.ACCEPTED]: 'Accepted',
  [APPLICATION_STATUSES.REJECTED]: 'Rejected',
};

/**
 * Status badge color classes (Tailwind)
 */
export const STATUS_BADGE_CLASSES = {
  [APPLICATION_STATUSES.PENDING_REVIEW]: 'bg-yellow-50 text-yellow-700 ring-1 ring-yellow-200',
  [APPLICATION_STATUSES.ACCEPTED]: 'bg-green-50 text-green-700 ring-1 ring-green-200',
  [APPLICATION_STATUSES.REJECTED]: 'bg-red-50 text-red-700 ring-1 ring-red-200',
};

/**
 * Status badge icons/emojis
 */
export const STATUS_BADGE_ICONS = {
  [APPLICATION_STATUSES.PENDING_REVIEW]: '⏳',
  [APPLICATION_STATUSES.ACCEPTED]: '✅',
  [APPLICATION_STATUSES.REJECTED]: '❌',
};

/**
 * Normalize legacy status values to new standard statuses
 * @param {string} status - Legacy or current status value
 * @returns {string} - Normalized status
 */
export function normalizeApplicationStatus(status) {
  if (!status) return DEFAULT_APPLICATION_STATUS;
  
  const normalized = String(status).toLowerCase().trim();
  
  // Map legacy statuses to new standard
  const statusMap = {
    'pending': APPLICATION_STATUSES.PENDING_REVIEW,
    'pending_review': APPLICATION_STATUSES.PENDING_REVIEW,
    'approved': APPLICATION_STATUSES.ACCEPTED,
    'accepted': APPLICATION_STATUSES.ACCEPTED,
    'rejected': APPLICATION_STATUSES.REJECTED,
    'shortlisted': APPLICATION_STATUSES.ACCEPTED, // Treat shortlisted as accepted
    'in_progress': APPLICATION_STATUSES.PENDING_REVIEW,
    'needs_revision': APPLICATION_STATUSES.PENDING_REVIEW,
    'awaiting_brand': APPLICATION_STATUSES.PENDING_REVIEW,
    'awaiting_influencer': APPLICATION_STATUSES.PENDING_REVIEW,
  };
  
  return statusMap[normalized] || APPLICATION_STATUSES.PENDING_REVIEW;
}

/**
 * Validate if a status is a valid application status
 * @param {string} status - Status to validate
 * @returns {boolean} - True if valid
 */
export function isValidApplicationStatus(status) {
  return Object.values(APPLICATION_STATUSES).includes(status);
}

/**
 * Get status badge configuration
 * @param {string} status - Application status
 * @returns {object} - Badge configuration with label, className, and icon
 */
export function getStatusBadgeConfig(status) {
  const normalized = normalizeApplicationStatus(status);
  return {
    label: STATUS_LABELS[normalized] || normalized,
    className: STATUS_BADGE_CLASSES[normalized] || STATUS_BADGE_CLASSES[APPLICATION_STATUSES.PENDING_REVIEW],
    icon: STATUS_BADGE_ICONS[normalized] || STATUS_BADGE_ICONS[APPLICATION_STATUSES.PENDING_REVIEW],
  };
}



